import { MouseEvent, useState } from 'react';
import { Button, Center, Icon, Pressable } from '~/commonComponents';
import Portal from '~/commonComponents/Portal';
import ShareIcon from '~/images/share-icon.svg';
import { IShift } from '~/models';
import { PosthogService, POSTHOG_EVENTS } from '~/services/posthog';
import { getInviteLocationCategory, type InviteSource } from '~/utils/posthog/inviteTracking';
import { InviteProfessionalPopup } from './Popup';

// InviteProfessional is used in Shifts/Schedule contexts
type InviteProfessionalSource = Extract<InviteSource,
  'schedule_list' | 'shifts_open' | 'shifts_action_needed' | 'shift_details'>;

interface IProps {
  shift: IShift;
  buttonText?: string;
  isRefetchJob?: boolean;
  size?: 'icon' | 'default';
  source?: InviteProfessionalSource;
}

export const InviteProfessional = ({
  shift,
  buttonText = 'Invite Professionals',
  isRefetchJob = true,
  size = 'default',
  source = 'shifts_open',
}: IProps) => {
  const [isInvitePopupOpen, setInvitePopupOpen] = useState(false);

  const handleInviteClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setInvitePopupOpen(true);

    // Track PostHog event with properties
    const posthog = PosthogService.getInstance();

    posthog.track(POSTHOG_EVENTS.OFFICE_INVITE_SENT, {
      source,
      invite_type: 'existing_shift',
      location_category: getInviteLocationCategory(source),
      shift_id: shift?.id,
      profession: shift?.profession,
      office_id: shift?.offer_owner?.id,
      shift_date: shift?.local_date,
    });
  };

  return (
    <>
      <Center width="100%" height="100%">
        {size === 'default' ? (
          <Button Icon={ShareIcon} text={buttonText} onClick={handleInviteClick} />
        ) : (
          <Pressable onPress={handleInviteClick}>
            <Icon name="share" size={24} />
          </Pressable>
        )}
      </Center>
      {isInvitePopupOpen && (
        <Portal>
          <div onClick={(e) => e.stopPropagation()}>
            <InviteProfessionalPopup
              isOpen={isInvitePopupOpen}
              isRefetchJob={isRefetchJob}
              jobId={shift?.id}
              profession={shift?.profession}
              officeId={shift?.offer_owner?.id}
              onClose={() => setInvitePopupOpen(false)}
            />
          </div>
        </Portal>
      )}
    </>
  );
};
